import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Table, Form, Alert, Spinner, Modal, Badge } from 'react-bootstrap'
import { api } from '../../services/api'

interface Webhook {
  id: string
  name: string
  url: string
  events: string[]
  isActive: boolean
  secret?: string
  createdAt: string
  lastTriggered?: string
  _count: {
    deliveries: number
  }
}

interface WebhookData {
  name: string
  url: string
  events: string[]
  secret: string
}

const availableEvents = [
  { value: 'ticket.created', label: 'Ticket Created' },
  { value: 'ticket.updated', label: 'Ticket Updated' },
  { value: 'ticket.status_changed', label: 'Ticket Status Changed' },
  { value: 'ticket.assigned', label: 'Ticket Assigned' },
  { value: 'ticket.comment_added', label: 'Comment Added' },
  { value: 'ticket.resolved', label: 'Ticket Resolved' },
  { value: 'ticket.closed', label: 'Ticket Closed' },
  { value: 'user.created', label: 'User Created' },
  { value: 'user.updated', label: 'User Updated' }
]

export const WebhooksPage = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null)
  const [webhookData, setWebhookData] = useState<WebhookData>({
    name: '',
    url: '',
    events: [],
    secret: ''
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchWebhooks()
  }, [])

  const fetchWebhooks = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/webhooks')
      setWebhooks(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load webhooks')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      if (editingWebhook) {
        await api.patch(`/admin/webhooks/${editingWebhook.id}`, webhookData)
      } else {
        await api.post('/admin/webhooks', webhookData)
      }
      setShowModal(false)
      setEditingWebhook(null)
      setWebhookData({ name: '', url: '', events: [], secret: '' })
      fetchWebhooks()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save webhook')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook)
    setWebhookData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      secret: webhook.secret || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (webhookId: string) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      try {
        await api.delete(`/admin/webhooks/${webhookId}`)
        fetchWebhooks()
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete webhook')
      }
    }
  }

  const handleToggleStatus = async (webhookId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/webhooks/${webhookId}`, { isActive: !isActive })
      fetchWebhooks()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update webhook')
    }
  }

  const handleTestWebhook = async (webhookId: string) => {
    try {
      await api.post(`/admin/webhooks/${webhookId}/test`)
      // Show success message
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to test webhook')
    }
  }

  const handleEventToggle = (event: string) => {
    setWebhookData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="webhooks-page">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">Webhooks</h2>
          <p className="text-muted mb-0 small">Configure webhooks to receive real-time notifications</p>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)} className="d-flex align-items-center">
          <i className="fas fa-plus me-2"></i>
          Add Webhook
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-primary">{webhooks.length}</h3>
              <p className="text-muted mb-0">Total Webhooks</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-success">{webhooks.filter(w => w.isActive).length}</h3>
              <p className="text-muted mb-0">Active Webhooks</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{webhooks.reduce((sum, w) => sum + w._count.deliveries, 0)}</h3>
              <p className="text-muted mb-0">Total Deliveries</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-warning">
                {webhooks.length > 0 ? Math.round(webhooks.reduce((sum, w) => sum + w._count.deliveries, 0) / webhooks.length) : 0}
              </h3>
              <p className="text-muted mb-0">Avg Deliveries</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Webhooks Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-link me-2 text-primary"></i>
            Webhooks ({webhooks.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {webhooks.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Webhook</th>
                  <th>URL</th>
                  <th>Events</th>
                  <th>Deliveries</th>
                  <th>Last Triggered</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((webhook) => (
                  <tr key={webhook.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{webhook.name}</div>
                        {webhook.secret && (
                          <small className="text-muted">Secured</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '200px' }}>
                        {webhook.url}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {webhook.events.slice(0, 2).map((event, index) => (
                          <Badge key={index} bg="secondary" className="small">
                            {event}
                          </Badge>
                        ))}
                        {webhook.events.length > 2 && (
                          <Badge bg="light" text="dark" className="small">
                            +{webhook.events.length - 2}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg="info">{webhook._count.deliveries}</Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {webhook.lastTriggered ? formatDate(webhook.lastTriggered) : 'Never'}
                      </span>
                    </td>
                    <td>
                      <Badge bg={webhook.isActive ? 'success' : 'danger'}>
                        {webhook.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatDate(webhook.createdAt)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEdit(webhook)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant="outline-info" 
                          size="sm"
                          onClick={() => handleTestWebhook(webhook.id)}
                        >
                          <i className="fas fa-play"></i>
                        </Button>
                        <Button 
                          variant={webhook.isActive ? "outline-warning" : "outline-success"} 
                          size="sm"
                          onClick={() => handleToggleStatus(webhook.id, webhook.isActive)}
                        >
                          <i className={`fas fa-${webhook.isActive ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(webhook.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-link text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No webhooks found</h5>
              <p className="text-muted">Create your first webhook using the button above</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Webhook Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false)
        setEditingWebhook(null)
        setWebhookData({ name: '', url: '', events: [], secret: '' })
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingWebhook ? 'Edit Webhook' : 'Add New Webhook'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Webhook Name</Form.Label>
              <Form.Control
                type="text"
                value={webhookData.name}
                onChange={(e) => setWebhookData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Ticket Notifications"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Webhook URL</Form.Label>
              <Form.Control
                type="url"
                value={webhookData.url}
                onChange={(e) => setWebhookData(prev => ({ ...prev, url: e.target.value }))}
                required
                placeholder="https://your-app.com/webhook"
              />
              <Form.Text className="text-muted">
                The URL where webhook events will be sent
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Events</Form.Label>
              <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {availableEvents.map((event) => (
                  <Form.Check
                    key={event.value}
                    type="checkbox"
                    label={event.label}
                    checked={webhookData.events.includes(event.value)}
                    onChange={() => handleEventToggle(event.value)}
                    className="mb-2"
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                Select which events should trigger this webhook
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Secret (Optional)</Form.Label>
              <Form.Control
                type="password"
                value={webhookData.secret}
                onChange={(e) => setWebhookData(prev => ({ ...prev, secret: e.target.value }))}
                placeholder="Enter secret for webhook authentication"
              />
              <Form.Text className="text-muted">
                Used to verify webhook authenticity
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false)
            setEditingWebhook(null)
            setWebhookData({ name: '', url: '', events: [], secret: '' })
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={submitting || !webhookData.name.trim() || !webhookData.url.trim() || webhookData.events.length === 0}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editingWebhook ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {editingWebhook ? 'Update Webhook' : 'Create Webhook'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

